import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonicModule, ModalController } from '@ionic/angular';

import { EXTERNAL_TASK_MANAGEMENT_TYPES } from 'src/app/constants';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule],
  selector: 'app-create-external-task-modal',
  templateUrl: './create-external-task-modal.component.html',
  styleUrls: ['./create-external-task-modal.component.scss'],
})
export class CreateExternalTaskModalComponent implements OnInit {
  @ViewChild('titleInput') titleInput;

  @Input() conversationId: string;
  @Input() conversationTitle: string;
  @Input() conversationSummary: string;
  @Input() slackLink: string;
  @Input() unthreadLink: string;

  public title: string = '';
  public description: string = '';
  public projectId: string = '';
  public assigneeId: string = '';
  public isLoading = false;

  // Provider-specific configurations
  public taskManagerType: string;
  public taskManagerName: string;
  public isJira: boolean = false;
  public isGithub: boolean = false;
  public isMonday: boolean = false;
  public isAsana: boolean = false;

  // JIRA specific
  public jiraIssueTypeId: string = '';
  public jiraIssueTypeName: string = '';
  public jiraProjectKey: string = '';
  public jiraProjects: any[] = [];
  public jiraIssueTypes: any[] = [];

  // GitHub specific
  public githubProjectName: string = '';
  public githubProjects: any[] = [];

  // Monday specific
  public mondayBoardId: string = '';
  public mondayBoards: any[] = [];

  // Asana specific
  public asanaProjects: any[] = [];
  public asanaProjectId: string = '';

  public selectedAssignee: any;

  constructor(
    private modalCtrl: ModalController,
    private apiService: ApiService,
    private authService: AuthService,
    private msgSrvc: MessageService,
    private searchablePopoverService: SearchablePopoverService,
  ) {}

  async ngOnInit() {
    const config = this.authService.tenant.externalTaskManagementConfig;
    this.taskManagerType = config?.type;
    this.taskManagerName = config?.name;

    // Set up initial values
    this.title = this.conversationTitle || '';
    this.setupDescription();

    // Determine provider type and load necessary data
    this.isJira = this.taskManagerType === EXTERNAL_TASK_MANAGEMENT_TYPES.JIRA;
    this.isGithub = this.taskManagerType === EXTERNAL_TASK_MANAGEMENT_TYPES.GITHUB;
    this.isMonday = this.taskManagerType === EXTERNAL_TASK_MANAGEMENT_TYPES.MONDAY;
    this.isAsana = this.taskManagerType === EXTERNAL_TASK_MANAGEMENT_TYPES.ASANA;

    if (this.isJira) {
      await this.loadJiraData();
    } else if (this.isGithub) {
      await this.loadGithubProjects();
    } else if (this.isMonday) {
      await this.loadMondayBoards();
    } else if (this.isAsana) {
      await this.loadAsanaProjects();
    }
  }

  ionViewDidEnter() {
    setTimeout(() => {
      this.titleInput?.setFocus();
    }, 500);
  }

  private setupDescription() {
    this.description = this.conversationSummary ?? '';
  }

  private async loadJiraData() {
    try {
      // Load JIRA projects and issue types if needed
      // This would require additional API endpoints
    } catch (error) {
      console.error('Failed to load JIRA data:', error);
    }
  }

  private async loadGithubProjects() {
    try {
      this.githubProjects = await this.apiService.getPromise('/external-task-management/get-github-projects');
    } catch (error) {
      console.error('Failed to load GitHub projects:', error);
      this.msgSrvc.showError('Failed to load GitHub projects');
    }
  }

  private async loadMondayBoards() {
    try {
      this.mondayBoards = await this.apiService.getPromise('/external-task-management/get-monday-boards');
    } catch (error) {
      console.error('Failed to load Monday boards:', error);
      this.msgSrvc.showError('Failed to load Monday boards');
    }
  }

  private async loadAsanaProjects() {
    try {
      this.asanaProjects = await this.apiService.getPromise('/external-task-management/get-asana-projects');
    } catch (error) {
      console.error('Failed to load Asana projects:', error);
      this.msgSrvc.showError('Failed to load Asana projects');
    }
  }

  async selectExternalTaskManagerUser() {
    this.searchablePopoverService.externalTaskManagerUsers({
      event,
      callback: (id: string, name: string, user: any) => {
        this.selectedAssignee = user;
      },
    });
  }

  async submit() {
    if (!this.title.trim()) {
      this.msgSrvc.show('Please enter a task title.');
      return;
    }

    // Validate provider-specific requirements
    if (this.isGithub && !this.githubProjectName) {
      this.msgSrvc.show('Please select a GitHub repository.');
      return;
    }

    if (this.isMonday && !this.mondayBoardId) {
      this.msgSrvc.show('Please select a Monday board.');
      return;
    }

    if (this.isJira && !this.jiraProjectKey) {
      this.msgSrvc.show('Please select a JIRA project.');
      return;
    }

    if (this.isAsana && !this.asanaProjectId) {
      this.msgSrvc.show('Please select an Asana project.');
      return;
    }

    this.isLoading = true;

    try {
      const providerConfig: any = {};

      // Set provider-specific configurations
      if (this.isJira) {
        if (this.jiraIssueTypeId) providerConfig.jiraIssueTypeId = this.jiraIssueTypeId;
        if (this.jiraIssueTypeName) providerConfig.jiraIssueTypeName = this.jiraIssueTypeName;
        if (this.jiraProjectKey) providerConfig.jiraProjectKey = this.jiraProjectKey;
      } else if (this.isGithub) {
        providerConfig.githubProjectName = this.githubProjectName;
      } else if (this.isMonday) {
        providerConfig.mondayBoardId = this.mondayBoardId;
      } else if (this.isAsana) {
        providerConfig.asanaProjectId = this.asanaProjectId;
      }

      const payload = {
        title: this.title.trim(),
        description: this.description.trim(),
        projectId: this.projectId || undefined,
        assigneeId: this.selectedAssignee?.id || undefined,
        providerConfig,
      };

      const result = await this.apiService.postPromise(`/conversations/${this.conversationId}/external-tasks`, payload);

      this.modalCtrl.dismiss({
        success: true,
        task: result,
      });

      this.msgSrvc.show('Task created successfully!');
    } catch (error) {
      console.error('Error creating external task:', error);
      this.msgSrvc.showError(error);
    } finally {
      this.isLoading = false;
    }
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  get modalTitle(): string {
    return `Create ${this.taskManagerName || 'External'} Task`;
  }

  get requiresProjectSelection(): boolean {
    return this.isGithub || this.isMonday || this.isJira || this.isAsana;
  }

  get projectSelectionLabel(): string {
    if (this.isGithub) return 'GitHub Repository';
    if (this.isMonday) return 'Monday Board';
    if (this.isJira) return 'JIRA Project';
    if (this.isAsana) return 'Asana Project';
    return 'Project';
  }

  get projectOptions(): any[] {
    if (this.isGithub) return this.githubProjects;
    if (this.isMonday) return this.mondayBoards;
    if (this.isJira) return this.jiraProjects;
    if (this.isAsana) return this.asanaProjects;
    return [];
  }

  get selectedProject(): string {
    if (this.isGithub) return this.githubProjectName;
    if (this.isMonday) return this.mondayBoardId;
    if (this.isJira) return this.jiraProjectKey;
    if (this.isAsana) return this.asanaProjectId;
    return '';
  }

  onProjectChange(value: string) {
    if (this.isGithub) {
      this.githubProjectName = value;
      this.projectId = value;
    } else if (this.isMonday) {
      this.mondayBoardId = value;
      this.projectId = value;
    } else if (this.isJira) {
      this.jiraProjectKey = value;
      this.projectId = value;
    } else if (this.isAsana) {
      this.asanaProjectId = value;
      this.projectId = value;
    }
  }
}
